import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import type { ArticleMedia } from '../types';

function parseDurationToSeconds(duration?: string): number {
  if (!duration) return 0;
  const [mm, ss] = duration.split(':').map(Number);
  return (mm || 0) * 60 + (ss || 0);
}

/**
 * Reproductor dentro de la vista de detalle.
 * - Video: play/pause real con barra de progreso.
 * - GIF: se asume en loop automático, sin controles (así se comporta un GIF real).
 *
 * Este componente asume un <video> propio (recurso hosteado por el equipo),
 * no un embed de YouTube/Vimeo/Loom. Si el video final viene de un proveedor
 * externo, la UI de controles cambia.
 */
export function MediaPlayer({ media }: { media: ArticleMedia }) {
  const isVideo = media.type === 'video';
  const isFoto = media.type === 'foto';
  const totalSeconds = parseDurationToSeconds(media.duration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  if (media.url && isVideo) {
    return (
      <div className="mb-4">
        <video src={media.url} controls className="w-full rounded-xl" />
      </div>
    );
  }

  if (media.url && (isFoto || media.type === 'gif')) {
    return (
      <div className="mb-4">
        <img src={media.url} alt="" className="w-full rounded-xl object-cover" />
      </div>
    );
  }

  function togglePlay() {
    if (!isVideo) return;
    if (isPlaying) {
      setIsPlaying(false);
      setProgressPct(0);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }
    setIsPlaying(true);
    requestAnimationFrame(() => setProgressPct(100));
    timeoutRef.current = setTimeout(() => {
      setIsPlaying(false);
      setProgressPct(0);
    }, totalSeconds * 1000);
  }

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
        className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl"
        style={{
          background:
            'linear-gradient(135deg, #1a2332 0%, #2b3a52 55%, #334155 100%)',
        }}
      >
        <span className="absolute left-2.5 top-2.5 rounded-md bg-black/45 px-2 py-1 text-micro font-bold tracking-wide text-white">
          {isVideo ? 'VIDEO' : isFoto ? 'FOTO' : 'GIF ANIMADO'}
        </span>
        {isVideo && media.duration && (
          <span className="absolute bottom-2.5 right-2.5 rounded-md bg-black/50 px-1.5 py-0.5 text-micro font-semibold text-white">
            {media.duration}
          </span>
        )}
        {isVideo && (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform hover:scale-105">
            {isPlaying ? (
              <Pause className="h-4 w-4 text-brand" fill="currentColor" />
            ) : (
              <Play className="h-4 w-4 text-brand" fill="currentColor" />
            )}
          </span>
        )}
        {isVideo && (
          <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/25">
            <span
              className="block h-full bg-brand"
              style={{
                width: `${progressPct}%`,
                transition: isPlaying
                  ? `width ${totalSeconds}s linear`
                  : 'none',
              }}
            />
          </span>
        )}
      </button>
      <p className="mt-2 text-label text-subtle">
        {isVideo
          ? 'Video explicativo · toca para reproducir'
          : isFoto
            ? 'Imagen de referencia'
            : 'Se reproduce automáticamente en bucle'}
      </p>
    </div>
  );
}

/** Badge pequeño sobre el ícono del artículo en la lista, indicando si trae video o GIF. */
export function MediaBadge({ media }: { media?: ArticleMedia }) {
  if (!media) return null;
  if (media.type === 'video') {
    return (
      <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-surface-0 bg-brand">
        <Play className="h-[7px] w-[7px] text-white" fill="currentColor" />
      </span>
    );
  }
  return (
    <span className="absolute -bottom-1 -right-1 flex h-3.5 min-w-[20px] items-center justify-center rounded border-2 border-surface-0 bg-neutral-80 px-0.5">
      <span className="text-[7.5px] font-bold tracking-wide text-white">{media.type === 'foto' ? 'FOTO' : 'GIF'}</span>
    </span>
  );
}
